import { ShowContextProvider, Title, useEditController } from "react-admin";
import { useParams } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { DateTime } from "luxon";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { FeedbackTypeChip, memberSearchHref } from "./feedbackTypes";
import { colors, fontFamilyMono } from "../../data/tokens";
import { initials } from "../../util/text";
import { useMemberName } from "../../util/useMemberName";

// Leitura do contato no estilo e-mail: cabeçalho com remetente/tipo/data
// e corpo da mensagem com quebras de linha preservadas.
export const FeedbacksShow = () => {
  const { id } = useParams();
  const controller: any = useEditController({
    resource: "feedbacks/crud",
    id,
  });
  const record = controller?.record;
  const name = String(record?.name || "").trim();
  const email = String(record?.email || "").trim();
  const isEmail = email.includes("@");
  // Nome real do sócio buscado do cadastro (estilo Gmail), com fallback
  // para o nome gravado no contato e depois para o identificador
  const memberName = useMemberName(email);
  const displayName = memberName || name || email;
  // Linha secundária só quando acrescenta informação (nome ≠ identificador)
  const showEmailLine = Boolean(
    displayName && email && displayName.toLowerCase() !== email.toLowerCase()
  );
  const dt = record?.createdAt ? DateTime.fromISO(record.createdAt) : null;
  const createdAt = dt?.isValid
    ? dt.setLocale("pt-BR").toFormat("dd/MM/yyyy 'às' HH:mm")
    : record?.createdAt
    ? new Date(record.createdAt).toLocaleDateString("pt-BR")
    : "";

  return (
    <ShowContextProvider value={controller}>
      <Title title="Contatos" />
      <PageTopBreadcrumb
        title="Contatos"
        firstPath="/#/feedbacks/crud"
        secondTitle="Visualizando"
        thirdTitle={email || undefined}
        actionType="edit"
      />
      {record && (
        <Card sx={{ marginBottom: 3 }}>
          {/* Box em vez de CardContent: o App.css zera o padding de todo
              .MuiCardContent-root com !important (as telas antigas compensam
              no Card); aqui o respiro fica sob controle deste componente */}
          <Box sx={{ padding: { xs: 2.5, md: 4 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              {/* Remetente clicável: abre o cadastro do sócio */}
              <Tooltip title={email ? "Ver cadastro do sócio" : ""}>
                <Box
                  component={email ? "a" : "div"}
                  href={email ? memberSearchHref(email) : undefined}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    minWidth: 0,
                    textDecoration: "none",
                    color: "inherit",
                    "&:hover .sender-name": {
                      textDecoration: email ? "underline" : "none",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      fontSize: 15,
                      fontWeight: 700,
                      backgroundColor: colors.bgContent,
                      color: colors.primaryDark,
                    }}
                  >
                    {showEmailLine
                      ? initials(displayName)
                      : isEmail
                      ? email[0]?.toUpperCase()
                      : null}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      className="sender-name"
                      noWrap
                      sx={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: colors.primaryDark,
                      }}
                    >
                      {displayName || "Sem identificação"}
                    </Typography>
                    {showEmailLine ? (
                      <Typography
                        noWrap
                        sx={{
                          fontSize: 12.5,
                          color: colors.textMuted,
                          fontFamily: isEmail ? undefined : fontFamilyMono,
                        }}
                      >
                        {email}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              </Tooltip>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  marginLeft: "auto",
                }}
              >
                <FeedbackTypeChip type={record.type} />
                {createdAt ? (
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      color: colors.textMuted,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {createdAt}
                  </Typography>
                ) : null}
              </Box>
            </Box>

            <Divider sx={{ marginY: 2.5 }} />

            <Typography
              sx={{
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                fontSize: 14.5,
                lineHeight: 1.75,
              }}
            >
              {record.message || "(sem mensagem)"}
            </Typography>

            <Divider sx={{ marginY: 2.5 }} />
            <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
              Registro #{record.id}
            </Typography>
          </Box>
        </Card>
      )}
    </ShowContextProvider>
  );
};
